import { callDelete, post, put } from "@/services/api.services";
import { SavedObject } from "@/services/commonModels";
import { SavedApplication } from "@/services/applications/applicationsAPI";
import { RegistryEntry, RegistrySchemaWithOptions } from "@/services/registries/registriesAPI";

/**
 * Marker interface used to work with saved factors of any type.
 */
export type SavedFactor = {
    id: string;
};

export type ApplicationFactor = {
    applicationId: string;
};

export type SavedApplicationFactor = ApplicationFactor & SavedObject<string> & SavedFactor;

export type ApplicationFactorDTO = SavedApplicationFactor & {
    // adds the thing_instance_data_id value from the mango.applications table
    thingInstanceDataId: string;
    name: string;
};

export type AdHocFactorType = {
    name: string;
};

export type SavedAdHocFactorType = AdHocFactorType &
    SavedFactor &
    SavedObject<string> & {
        experimentId: number;
    };

export type AdHocFactor = {
    value: string;
    sortOrder: number;
    adHocFactorTypeId: string;
};

export type SavedAdHocFactor = AdHocFactor & SavedObject<string> & SavedFactor;

/**
 * Groups the ad hoc factors by type, used to view ad hoc factors as a group
 */
export type AdHocFactorTypeDTO = SavedAdHocFactorType & {
    adHocFactors: SavedAdHocFactor[];
};

export type GlobalRegistryFactor = {
    globalRegistryFactorTypeId: string;
    globalRegistryEntryId: string;
};

export type SavedGlobalRegistryFactor = GlobalRegistryFactor &
    SavedObject<string> & {
        experimentId: number;
    };

export type SavedGlobalRegistryFactorDTO = SavedGlobalRegistryFactor & {
    registryEntry: RegistryEntry;
};

export type GlobalRegistryFactorType = {
    globalRegistryId: string;
};

export type SavedGlobalRegistryFactorType = GlobalRegistryFactorType & SavedObject<string>;

export type GlobalRegistryFactorTypeDTO = SavedGlobalRegistryFactorType & {
    globalRegistry: RegistrySchemaWithOptions;
};

export type GlobalRegistryFactorTypeWithFactorsDTO = GlobalRegistryFactorTypeDTO & {
    globalRegistryFactors: SavedGlobalRegistryFactor[];
};

export type AllExperimentFactorsConfig = {
    applications: SavedApplication[];
    applicationFactors: ApplicationFactorDTO[];
    adHocFactorTypes: AdHocFactorTypeDTO[];
    globalRegistryFactorTypes: GlobalRegistryFactorTypeWithFactorsDTO[];
};

export const conditionFactorsAPI = {
    /**
     * Replaces the current application factors with the given set of applications to be
     * associated as factors
     */
    saveApplicationFactors(param: { experimentCode: string; applications: SavedApplication[] }) {
        const { experimentCode, applications } = param;
        return post(`/conditionFactors/${experimentCode}/applicationFactors`, applications);
    },

    async saveAdHocFactorType(param: {
        experimentCode: string;
        adHocFactorType: AdHocFactorType | SavedAdHocFactorType;
    }): Promise<AllExperimentFactorsConfig> {
        const { experimentCode, adHocFactorType } = param;
        if ("id" in adHocFactorType) {
            return await this.updateAdHocFactorType({ experimentCode, adHocFactorType });
        } else {
            return await this.createAdHocFactorType({ experimentCode, adHocFactorType });
        }
    },

    createAdHocFactorType(param: {
        experimentCode: string;
        adHocFactorType: AdHocFactorType;
    }): Promise<AllExperimentFactorsConfig> {
        const { experimentCode, adHocFactorType } = param;
        return post(`/conditionFactors/${experimentCode}/adHocFactorTypes`, adHocFactorType);
    },

    updateAdHocFactorType(param: {
        experimentCode: string;
        adHocFactorType: SavedAdHocFactorType;
    }): Promise<AllExperimentFactorsConfig> {
        const { experimentCode, adHocFactorType } = param;
        return post(
            `/conditionFactors/${experimentCode}/adHocFactorTypes/${adHocFactorType.id}`,
            adHocFactorType
        );
    },

    deleteAdHocFactorType(param: {
        experimentCode: string;
        adHocFactorType: SavedAdHocFactorType;
    }): Promise<AllExperimentFactorsConfig> {
        const { experimentCode, adHocFactorType } = param;
        return callDelete(
            `/conditionFactors/${experimentCode}/adHocFactorTypes/${adHocFactorType.id}`
        );
    },

    createAdHocFactor(param: {
        experimentCode: string;
        adHocFactor: AdHocFactor;
    }): Promise<AllExperimentFactorsConfig> {
        const { experimentCode, adHocFactor } = param;
        return post(`/conditionFactors/${experimentCode}/adHocFactors`, adHocFactor);
    },

    updateAdHocFactor(param: {
        experimentCode: string;
        adHocFactor: SavedAdHocFactor;
    }): Promise<AllExperimentFactorsConfig> {
        const { experimentCode, adHocFactor } = param;
        return put(
            `/conditionFactors/${experimentCode}/adHocFactors/${adHocFactor.id}`,
            adHocFactor
        );
    },

    deleteAdHocFactor(param: {
        experimentCode: string;
        adHocFactor: SavedAdHocFactor;
    }): Promise<AllExperimentFactorsConfig> {
        const { experimentCode, adHocFactor } = param;
        return callDelete(`/conditionFactors/${experimentCode}/adHocFactors/${adHocFactor.id}`);
    },

    createGlobalRegistryFactorType(param: {
        experimentCode: string;
        globalRegistryFactorType: GlobalRegistryFactorType;
    }): Promise<AllExperimentFactorsConfig> {
        const { experimentCode, globalRegistryFactorType } = param;
        return post(
            `/conditionFactors/${experimentCode}/globalRegistryFactorTypes`,
            globalRegistryFactorType
        );
    },

    deleteGlobalRegistryFactorType(param: {
        experimentCode: string;
        globalRegistryFactorType: SavedGlobalRegistryFactorType;
    }): Promise<AllExperimentFactorsConfig> {
        const { experimentCode, globalRegistryFactorType } = param;
        return callDelete(
            `/conditionFactors/${experimentCode}/globalRegistryFactorTypes/${globalRegistryFactorType.id}`
        );
    },

    createGlobalRegistryFactor(param: {
        experimentCode: string;
        globalRegistryFactor: GlobalRegistryFactor;
    }): Promise<AllExperimentFactorsConfig> {
        const { experimentCode, globalRegistryFactor } = param;
        return post(
            `/conditionFactors/${experimentCode}/globalRegistryFactors`,
            globalRegistryFactor
        );
    },

    deleteGlobalRegistryFactor(param: {
        experimentCode: string;
        globalRegistryFactor: SavedGlobalRegistryFactor;
    }): Promise<AllExperimentFactorsConfig> {
        const { experimentCode, globalRegistryFactor } = param;
        return callDelete(
            `/conditionFactors/${experimentCode}/globalRegistryFactors/${globalRegistryFactor.id}`
        );
    },
};
